/**
 * Copyright (c) 2021-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { Badge, PageContent } from "@ndla/primitives";
import { styled } from "@ndla/styled-system/jsx";
import {
  ArticleByline,
  ArticleContent,
  ArticleFooter,
  ArticleTitle,
  ArticleWrapper,
  HomeBreadcrumb,
  licenseAttributes,
} from "@ndla/ui";
import { NoSSR } from "@ndla/util";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Article } from "../../components/Article/Article";
import { FavoriteButton } from "../../components/Article/FavoritesButton";
import { CompetenceGoals } from "../../components/CompetenceGoals";
import { LicenseBox, useArticleCopyText } from "../../components/license/LicenseBox";
import { AddResourceToFolderModal } from "../../components/MyNdla/AddResourceToFolderModal";
import { PageTitle } from "../../components/PageTitle";
import { RestrictedBlock } from "../../components/RestrictedBlock";
import { useRestrictedMode } from "../../components/RestrictedModeContext";
import { SocialMediaMetadata } from "../../components/SocialMediaMetadata";
import { SubjectLinkSet } from "../../components/Subject/SubjectLinks";
import config from "../../config";
import { SKIP_TO_CONTENT_ID } from "../../constants";
import type { GQLMultidisciplinarySubjectArticle_NodeFragment } from "../../graphqlTypes";
import { toBreadcrumbItems } from "../../routeHelpers";
import { getArticleScripts } from "../../util/getArticleScripts";
import { useListItemTraits } from "../../util/listItemTraits";
import { useRawLocation } from "../../util/localePath";
import { htmlTitle } from "../../util/titleHelper";
import { transformArticle } from "../../util/transformArticle";
import { Resources } from "../Resources/Resources";

const ResourcesPageContent = styled("div", {
  base: {
    position: "relative",
    background: "background.subtle",
    paddingBlock: "xxlarge",
    zIndex: "base",
    _after: {
      content: '""',
      position: "absolute",
      top: "0",
      bottom: "0",
      left: "[-100vw]",
      right: "[-100vw]",
      zIndex: "hide",
      background: "[inherit]",
    },
  },
});

const StyledArticleContent = styled(ArticleContent, {
  base: {
    overflowX: "visible",
  },
});

const StyledPageContent = styled(PageContent, {
  base: {
    overflowX: "clip",
    paddingBlockStart: "xxlarge",
    gap: "xsmall",
  },
});

const HeaderWrapper = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xsmall",
    alignItems: "flex-start",
  },
});

interface Props {
  node: GQLMultidisciplinarySubjectArticle_NodeFragment;
}

export const MultidisciplinarySubjectArticle = ({ node }: Props) => {
  const { t, i18n } = useTranslation();
  const crumbs = useMemo(() => node.context?.parents ?? [], [node]);
  const root = crumbs[0];
  const restrictedInfo = useRestrictedMode();
  const { pathname } = useRawLocation();

  const metaTitle = useMemo(
    () => htmlTitle(node.article?.title ?? node.name, [root?.name]),
    [node.article?.title, node.name, root?.name],
  );
  const pageTitle = useMemo(() => htmlTitle(metaTitle, [t("htmlTitles.titleTemplate")]), [metaTitle, t]);

  const breadCrumbs = useMemo(() => {
    return toBreadcrumbItems(t("breadcrumb.toFrontpage"), [...crumbs, node]);
  }, [t, node, crumbs]);

  const [article, scripts] = useMemo(() => {
    if (!node.article) return [undefined, undefined];
    return [
      transformArticle(node.article, i18n.language, {
        path: `${config.ndlaFrontendDomain}/article/${node.article.id}`,
        subject: root?.id,
        articleLanguage: node.article.language,
      }),
      getArticleScripts(node.article.requiredLibraries, node.article.transformedContent.content, i18n.language),
    ];
  }, [node.article, i18n.language, root?.id]);

  const copyText = useArticleCopyText(article);

  const traits = useListItemTraits({
    traits: article?.traits,
    resourceTypes: node.resourceTypes,
    relevanceId: node.relevanceId,
  });

  if (!node.article || !article) {
    return null;
  }

  const subjectLinks = node.article.crossSubjectTopics?.map((crossSubjectTopic) => ({
    name: crossSubjectTopic.title,
    url: crossSubjectTopic.url || root?.url || "",
  }));

  const authors =
    article.copyright?.creators.length || article.copyright?.rightsholders.length
      ? article.copyright.creators
      : article.copyright?.processors;

  const licenseProps = licenseAttributes(article.copyright?.license?.license, article.language, undefined);

  const socialMediaMetaData = {
    title: metaTitle,
    description: node.article?.metaDescription ?? node.article?.introduction,
    image: node.article?.metaImage,
  };

  return (
    <StyledPageContent variant="article" asChild consumeCss>
      <main>
        {scripts?.map((script) => (
          <script key={script.src} src={script.src} type={script.type} async={script.async} defer={script.defer} />
        ))}
        {!!node.context?.isArchived && <meta name="robots" content="noindex" />}
        <meta name="pageid" content={`${article.id}`} />
        <PageTitle title={pageTitle} trackingProps={node.context} />
        <SocialMediaMetadata
          title={socialMediaMetaData.title}
          description={socialMediaMetaData.description}
          imageUrl={socialMediaMetaData.image?.image.imageUrl}
          trackableContent={{
            supportedLanguages: node.article?.supportedLanguages,
          }}
          canonicalPath={node.context?.url}
        />
        <HeaderWrapper>
          <HomeBreadcrumb items={breadCrumbs.map((bc) => ({ ...bc, to: bc.url }))} />
          {!!subjectLinks?.length && (
            <SubjectLinkSet
              set="test"
              title={t("multidisciplinarySubject.subjectsLinksDescription")}
              subjects={subjectLinks}
            />
          )}
        </HeaderWrapper>
        <PageContent variant="content" gutters="never" asChild>
          <ArticleWrapper {...licenseProps}>
            <ArticleTitle
              id={SKIP_TO_CONTENT_ID}
              title={article.transformedContent.title}
              introduction={article.transformedContent.introduction}
              badges={
                traits.length ? traits.map((trait) => <Badge key={`${article.id}-${trait}`}>{trait}</Badge>) : undefined
              }
              competenceGoals={
                !!article.grepCodes?.filter((gc) => gc.toUpperCase().startsWith("K")).length && (
                  <CompetenceGoals
                    codes={article.grepCodes}
                    subjectId={root?.id}
                    supportedLanguages={article.supportedLanguages}
                  />
                )
              }
              lang={article.language}
              heartButton={
                !!node.url &&
                !!article.id && (
                  <AddResourceToFolderModal
                    resource={{
                      id: `${article.id}`,
                      path: node.url,
                      resourceType: "multidisciplinary",
                    }}
                  >
                    <FavoriteButton path={node.url} />
                  </AddResourceToFolderModal>
                )
              }
            />
            <StyledArticleContent>
              {restrictedInfo.restricted ? <RestrictedBlock /> : (article.transformedContent.content ?? "")}
            </StyledArticleContent>
            {!restrictedInfo.restricted && (
              <ArticleFooter>
                <ArticleByline
                  // re-render accordions when navigating to new article
                  key={pathname}
                  footnotes={article.transformedContent.metaData?.footnotes ?? []}
                  authors={authors}
                  suppliers={article.copyright?.rightsholders}
                  published={article.revised}
                  licenseBox={<LicenseBox article={article} copyText={copyText} oembed={article.oembed} />}
                />
                <NoSSR fallback={null}>
                  <ResourcesPageContent>
                    <Resources parentId={node.id} rootId={node.context?.rootId} />
                  </ResourcesPageContent>
                </NoSSR>
              </ArticleFooter>
            )}
          </ArticleWrapper>
        </PageContent>
      </main>
    </StyledPageContent>
  );
};

MultidisciplinarySubjectArticle.fragments = {
  node: gql`
    fragment MultidisciplinarySubjectArticle_Node on Node {
      id
      nodeType
      name
      url
      relevanceId
      context {
        contextId
        rootId
        breadcrumbs
        url
        defaultUrl
        isArchived
        parents {
          contextId
          id
          name
          url
        }
      }
      resourceTypes {
        id
        name
      }
      article {
        id
        language
        created
        updated
        revised
        oembed
        introduction
        metaDescription
        traits
        revision
        requiredLibraries {
          url
          mediaType
        }
        metaImage {
          id
          image {
            imageUrl
          }
          alttext {
            alttext
          }
        }
        crossSubjectTopics(subjectId: $rootId) {
          title
          path
          url
        }
        ...Article_Article
      }
    }
    ${Article.fragments.article}
  `,
};
